import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useClaimTypesApi } from "../../../http/claimTypes/api";
import { ClaimType } from "../../../http/claimTypes/types";
import { PaginationQueryWithFields } from "../../../types";
import { Action, getDeleteAction, getEditAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import Table from "../../ui/Table";
import CreateClaimTypeDialog from "./CreateClaimTypeDialog";
import DeleteClaimTypeDialog from "./DeleteClaimTypeDialog";
import EditClaimTypeDialog from "./EditClaimTypeDialog";

const CLAIM_TYPE_FIELDS = ["id", "name"] as const;
type ClaimTypeField = (typeof CLAIM_TYPE_FIELDS)[number];
type ClaimTypeSearch = Pick<ClaimType, ClaimTypeField>;

type ClaimTypesProps = {
  onClose: () => void;
};

const ClaimTypes: FC<ClaimTypesProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const claimTypesApi = useClaimTypesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "ct" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToEdit, setItemToEdit] = useState<ClaimTypeSearch>();
  const [itemToDelete, setItemToDelete] = useState<ClaimTypeSearch>();

  const query: PaginationQueryWithFields<ClaimTypeSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CLAIM_TYPE_FIELDS,
  };

  const result = claimTypesApi.useAdminClaimTypes(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: ClaimTypeSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.claimTypes.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    if (userSettings.permissions.claimTypes.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.claimTypes.plural}
        preTitleButton={userSettings.permissions.claimTypes.insert && <CreateClaimTypeDialog />}
      >
        <Table<ClaimTypeSearch>
          filters={filters}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="name" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            if (userSettings.permissions.claimTypes.update) {
              setItemToEdit(item);
            }
          }}
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToEdit && <EditClaimTypeDialog claimTypeId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />}

      {!!itemToDelete && (
        <DeleteClaimTypeDialog claimTypeId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}
    </>
  );
};

export default ClaimTypes;
